import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, FileText, Briefcase, Settings, Users, Search, Upload, LogOut, Shield, Inbox } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/content', icon: FileText, label: 'Page Content' },
  { to: '/admin/jobs', icon: Briefcase, label: 'Job Openings' },
  { to: '/admin/applications', icon: Inbox, label: 'Applications' },
  { to: '/admin/seo', icon: Search, label: 'SEO Metadata' },
  { to: '/admin/documents', icon: Upload, label: 'Documents' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

const ADMIN_NAV = [
  { to: '/admin/users', icon: Users, label: 'Users', role: 'superadmin' },
  { to: '/admin/audit', icon: Shield, label: 'Audit Log', role: 'superadmin' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Sidebar */}
      <aside className="w-56 bg-[#111] border-r border-white/10 flex flex-col shrink-0" style={{ minHeight: '100vh' }}>
        <div className="p-4 border-b border-white/10">
          <img src="/logo-cropped.png" alt="SISL" className="h-10 object-contain" />
          <p className="text-[10px] text-[#555] mt-1 uppercase tracking-widest">Admin Panel</p>
        </div>

        <nav className="flex-1 py-4 space-y-0.5 px-2">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors ${
                  isActive ? 'bg-[#F2B705]/10 text-[#F2B705]' : 'text-[#888] hover:text-white hover:bg-white/5'
                }`
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}

          {user?.role === 'superadmin' && (
            <>
              <div className="border-t border-white/5 mt-3 pt-3 px-3">
                <p className="text-[10px] text-[#444] uppercase tracking-widest mb-2">Admin</p>
              </div>
              {ADMIN_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors ${
                      isActive ? 'bg-[#F2B705]/10 text-[#F2B705]' : 'text-[#888] hover:text-white hover:bg-white/5'
                    }`
                  }
                >
                  <item.icon size={16} />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-[#666] mb-2">
            <p className="text-white font-medium">{user?.name}</p>
            <p className="text-[10px] uppercase tracking-wide text-[#F2B705]">{user?.role}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-xs text-[#666] hover:text-red-400 transition-colors w-full">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-8 overflow-y-auto" style={{ maxHeight: '100vh' }}>
        <Outlet />
      </main>
    </div>
  );
}
